/* LRS Editorials: reversible native scroll scenes. Photography, text and links remain native without this enhancement. */
(() => {
  'use strict';
  if (window.LRSEditorials) return;

  const ROOT = '[data-lrs-editorials]';
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const contrast = matchMedia('(prefers-contrast: more), (forced-colors: active)');
  const clamp = value => Math.max(0, Math.min(1, value));
  const smooth = value => { const p = clamp(value); return p * p * (3 - 2 * p); };
  const scrollPosition = () => Math.max(0, window.scrollY || 0);
  const allowed = root => !reduced.matches && !contrast.matches &&
    root.dataset.edMotion !== 'false' && document.body.dataset.lrsMotion !== 'false' &&
    !window.Shopify?.designMode && !document.body.classList.contains('shopify-design-mode') &&
    !document.documentElement.classList.contains('shopify-design-mode');

  function headerInset() {
    const height = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--lrs-header-height'));
    return Math.max(0, Number.isFinite(height) ? height : document.querySelector('.site-header')?.getBoundingClientRect().height || 76) + 20;
  }

  function splitCopy(element) {
    const replacements = [];
    const words = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.textContent.trim() || node.parentElement?.closest('script,style,noscript,textarea,input,select,button,[aria-hidden="true"],[data-ed-copy]') !== element) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    const originals = [];
    while (walker.nextNode()) originals.push(walker.currentNode);
    originals.forEach(original => {
      const fragment = document.createDocumentFragment();
      const nodes = [];
      (window.LRSI18n?.words(original.textContent) || original.textContent.split(/(\s+)/u)).filter(Boolean).forEach(part => {
        const node = /\S/u.test(part) ? document.createElement('span') : document.createTextNode(part);
        if (node.nodeType === Node.ELEMENT_NODE) {
          node.className = 'lrs-ed-word';
          node.textContent = part;
          words.push({ element: node, top: 0, left: 0, value: '' });
        }
        fragment.append(node);
        nodes.push(node);
      });
      const parent = original.parentNode;
      parent.replaceChild(fragment, original);
      replacements.push({ original, parent, nodes });
    });
    return { element, words, replacements, scene: null, top: 0, height: 0, left: 0, width: 1 };
  }

  function restoreCopy(copy) {
    copy.replacements.forEach(({ original, parent, nodes }) => {
      const first = nodes.find(node => node.parentNode === parent);
      if (first) parent.insertBefore(original, first);
      nodes.forEach(node => { if (node.parentNode === parent) parent.removeChild(node); });
    });
    copy.element.removeAttribute('data-ed-copy-progress');
  }

  function create(root) {
    if (instances.has(root)) return;
    let destroyed = false;
    let frame = 0;
    let needsMeasure = true;
    let resizeObserver = null;
    let anchor = null;
    let anchorUntil = 0;
    let viewport = innerHeight;
    let topInset = 96;
    const cleanups = [];
    const copies = [];
    const photos = [];
    const scenes = [];
    const enhance = allowed(root) && getComputedStyle(root).getPropertyValue('--lrs-ed-motion-ready').trim() === '1';
    const belongs = node => node.closest(ROOT) === root;

    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    function schedule(measure = false) {
      if (destroyed) return;
      if (measure) needsMeasure = true;
      if (!frame) frame = requestAnimationFrame(paint);
    }

    function clearScene(scene) {
      scene.pinned = false;
      scene.element.dataset.edPin = 'flow';
      scene.element.style.removeProperty('--ed-scene-height');
      scene.element.style.removeProperty('--ed-stage-top');
    }

    function measure() {
      needsMeasure = false;
      viewport = Math.max(1, document.documentElement.clientHeight || innerHeight);
      topInset = headerInset();
      const bottomInset = innerWidth <= 700 ? 48 : 28;
      const available = Math.max(0, viewport - topInset - bottomInset);
      const pace = Math.max(300, Math.min(900, parseFloat(root.dataset.edPace) || 600));
      const y = scrollPosition();
      const animationAllowed = enhance && allowed(root);
      root.classList.toggle('is-ed-motion', animationAllowed);
      root.style.setProperty('--ed-photo-fit-height', `${Math.max(200, available - 50)}px`);

      scenes.forEach(scene => {
        const stageStyles = getComputedStyle(scene.stage);
        scene.marginTop = parseFloat(stageStyles.marginTop) || 0;
        scene.marginBottom = parseFloat(stageStyles.marginBottom) || 0;
        const stageHeight = scene.stage.getBoundingClientRect().height;
        const outerHeight = stageHeight + scene.marginTop + scene.marginBottom;
        const hasContents = scene.words.length >= 14 || scene.photos.length > 0;
        // Keep tall/mobile diptychs in the normal page flow. Their photos each
        // reveal from their own viewport position, including the second frame.
        const canPin = animationAllowed && hasContents && available >= 200 &&
          stageHeight > 0 && outerHeight <= available &&
          CSS.supports('position', 'sticky') && !scene.stage.matches('form') && !scene.stage.querySelector('form');
        if (!canPin) { clearScene(scene); return; }
        scene.pinned = true;
        scene.distance = Math.round(Math.min(scene.photos.length ? 700 : 850,
          Math.max(scene.photos.length ? 300 : 350, scene.photos.length ? pace * .8 : Math.min(pace, 220 + scene.words.length * 7))));
        // Article scenes settle in the centre of the usable reading viewport.
        scene.inset = Math.round(topInset + (root.hasAttribute('data-lrs-article-reader') ? Math.max(0, (available - outerHeight) / 2) : 0));
        scene.element.dataset.edPin = 'sticky';
        scene.element.style.setProperty('--ed-scene-height', `${Math.ceil(outerHeight + scene.distance)}px`);
        scene.element.style.setProperty('--ed-stage-top', `${scene.inset}px`);
      });

      // Read positions after every runway has been sized. Scroll painting then
      // uses cached document coordinates, without measuring every word per frame.
      scenes.forEach(scene => {
        scene.top = y + scene.element.getBoundingClientRect().top;
        scene.height = scene.element.getBoundingClientRect().height;
      });
      copies.forEach(copy => {
        const copyRect = copy.element.getBoundingClientRect();
        copy.top = y + copyRect.top;
        copy.height = copyRect.height;
        copy.left = copyRect.left;
        copy.width = copyRect.width;
        if (!copy.scene?.pinned) {
          copy.words.forEach(word => {
            const rect = word.element.getBoundingClientRect();
            const offset = parseFloat(word.element.style.getPropertyValue('--ed-word-y')) || 0;
            const translation = offset ? offset * (parseFloat(getComputedStyle(word.element).fontSize) || 0) : 0;
            word.top = y + rect.top - translation;
            word.left = rect.left;
          });
        }
      });
      photos.forEach(photo => {
        const translation = parseFloat(photo.element.style.getPropertyValue('--ed-photo-y')) || 0;
        photo.top = y + photo.element.getBoundingClientRect().top - translation;
      });
    }

    function isForced(element, active) {
      return Boolean(active && element.contains(active)) || Boolean(anchor &&
        (anchor === root || element === anchor || element.contains(anchor)));
    }

    function wordValue(word, value) {
      const q = smooth(value);
      const opacity = q.toFixed(3);
      if (word.value === opacity) return;
      word.value = opacity;
      word.element.style.setProperty('--ed-word-opacity', opacity);
      word.element.style.setProperty('--ed-word-y', `${((1 - q) * .18).toFixed(3)}em`);
    }

    function paintPhoto(photo, progress, forced) {
      const p = forced ? 1 : smooth(progress);
      const opening = photo.opening;
      const value = p.toFixed(4);
      if (photo.value === value) return;
      photo.value = value;
      photo.element.style.setProperty('--ed-photo-progress', value);
      photo.element.style.setProperty('--ed-photo-opacity', (opening ? .84 + .16 * p : p).toFixed(4));
      photo.element.style.setProperty('--ed-photo-inset', `${((1 - p) * (opening ? 3 : 14)).toFixed(3)}%`);
      photo.element.style.setProperty('--ed-photo-y', `${((1 - p) * (opening ? 6 : 18)).toFixed(3)}px`);
      photo.element.style.setProperty('--ed-photo-scale', (1 + (1 - p) * (opening ? .008 : .025)).toFixed(5));
    }

    function paint() {
      frame = 0;
      if (destroyed || !root.isConnected) return;
      if (needsMeasure) measure();
      const y = scrollPosition();
      const active = root.contains(document.activeElement) ? document.activeElement : null;
      if (anchor && performance.now() > anchorUntil && !root.contains(document.activeElement)) anchor = null;
      if (!enhance || !allowed(root)) return;

      scenes.forEach(scene => {
        scene.progress = scene.pinned ? clamp((y - (scene.top + scene.marginTop - scene.inset)) / scene.distance) :
          clamp((y + viewport * .88 - scene.top) / Math.max(viewport * .58, scene.height));
        scene.forced = isForced(scene.element, active);
        scene.element.dataset.edProgress = (scene.forced ? 1 : scene.progress).toFixed(4);
        if (scene.pinned) {
          const count = scene.words.length;
          scene.words.forEach((word, index) => wordValue(word, scene.forced ? 1 :
            (scene.progress - .03 - (count > 1 ? index / (count - 1) : 0) * .69) / .18));
        }
      });
      copies.forEach(copy => {
        if (copy.scene?.pinned) return;
        const forced = isForced(copy.element, active) || copy.scene?.forced;
        const left = copy.left;
        let progress = 0;
        const startsInFirstViewport = copy.words.length < 14 && copy.top + copy.height <= viewport * .88;
        copy.words.forEach((word, index) => {
          // Reading rows reveal gently as they enter. A small horizontal phase
          // preserves individual word motion on long passages that cannot pin.
          const phase = Math.max(0, word.left - left) / Math.max(1, copy.width) * viewport * .055;
          const firstStart = copy.words.length > 1 ? index / (copy.words.length - 1) * 30 : 0;
          const q = forced ? 1 : startsInFirstViewport ?
            (y - firstStart) / Math.min(150, Math.max(90, viewport * .17)) :
            (y + viewport * .88 - word.top - phase) / Math.max(90, viewport * .20);
          wordValue(word, q);
          progress += clamp(q);
        });
        copy.element.dataset.edCopyProgress = (copy.words.length ? progress / copy.words.length : 1).toFixed(4);
      });
      photos.forEach(photo => {
        const scene = photo.scene;
        const progress = scene?.pinned ? (scene.progress - .035) / .82 :
          (y + viewport * .91 - photo.top) / Math.max(180, viewport * .59);
        paintPhoto(photo, progress, isForced(photo.element, active) || scene?.forced);
      });
    }

    function revealHash(scrollToTarget = false) {
      if (!location.hash || location.hash.length < 2) return;
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch (_) { return; }
      const target = document.getElementById(id);
      if (!target || !root.contains(target)) return;
      anchor = target;
      anchorUntil = performance.now() + 1000;
      if (needsMeasure) measure();
      const scene = scenes.find(item => item.element.contains(target));
      if (scrollToTarget && scene?.pinned) {
        window.scrollTo({ top: Math.max(0, scene.top + scene.marginTop - scene.inset + scene.distance), behavior: 'instant' });
      } else if (scrollToTarget && target === root) {
        window.scrollTo({ top: Math.max(0, scrollPosition() + root.getBoundingClientRect().top - topInset), behavior: 'instant' });
      }
      schedule();
    }

    if (enhance) {
      root.querySelectorAll('[data-ed-copy]').forEach(element => {
        if (!belongs(element) || element.closest('form')) return;
        copies.push(splitCopy(element));
      });
      root.querySelectorAll('[data-ed-photo]').forEach(element => {
        if (!belongs(element) || element.closest('form')) return;
        element.classList.add('is-ed-photo-motion');
        photos.push({ element, opening: element.hasAttribute('data-ed-opening'), scene: null, top: 0 });
      });
      root.querySelectorAll('[data-ed-scene]').forEach(element => {
        if (!belongs(element)) return;
        const stage = element.querySelector('[data-ed-stage]');
        if (!stage || stage.closest('[data-ed-scene]') !== element) return;
        const scene = { element, stage, copies: [], photos: [], words: [], pinned: false, distance: 1, top: 0, height: 1, marginTop: 0, marginBottom: 0, progress: 0, forced: false };
        scene.copies = copies.filter(copy => copy.element.closest('[data-ed-scene]') === element);
        scene.photos = photos.filter(photo => photo.element.closest('[data-ed-scene]') === element);
        scene.copies.forEach(copy => { copy.scene = scene; scene.words.push(...copy.words); });
        scene.photos.forEach(photo => { photo.scene = scene; });
        scenes.push(scene);
      });
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => schedule(true));
        scenes.forEach(scene => resizeObserver.observe(scene.stage));
        copies.filter(copy => !copy.scene).forEach(copy => resizeObserver.observe(copy.element));
      }
      listen(root, 'load', event => { if (event.target instanceof HTMLImageElement) schedule(true); }, true);
      listen(root, 'focusin', () => schedule());
      listen(root, 'focusout', () => schedule());
    }

    const destroy = () => {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      cleanups.forEach(cleanup => cleanup());
      copies.forEach(restoreCopy);
      scenes.forEach(scene => {
        delete scene.element.dataset.edPin;
        delete scene.element.dataset.edProgress;
        scene.element.style.removeProperty('--ed-scene-height');
        scene.element.style.removeProperty('--ed-stage-top');
      });
      photos.forEach(photo => {
        photo.element.classList.remove('is-ed-photo-motion');
        ['--ed-photo-progress', '--ed-photo-opacity', '--ed-photo-inset', '--ed-photo-y', '--ed-photo-scale'].forEach(name => photo.element.style.removeProperty(name));
      });
      root.classList.remove('is-ed-motion');
      root.style.removeProperty('--ed-photo-fit-height');
    };
    instances.set(root, { destroy, measure: () => schedule(true) });
    const rootObserver = new MutationObserver(() => {
      destroy();
      instances.delete(root);
      if (root.isConnected) create(root);
    });
    rootObserver.observe(root, { attributes: true, attributeFilter: ['data-ed-motion', 'data-ed-pace'] });
    cleanups.push(() => rootObserver.disconnect());
    listen(window, 'scroll', () => schedule(), { passive: true });
    listen(window, 'resize', () => schedule(true), { passive: true });
    listen(window, 'pageshow', () => schedule(true));
    listen(window, 'hashchange', () => revealHash(true));
    listen(window, 'load', () => { schedule(true); revealHash(true); }, { once: true });
    document.fonts?.ready.then(() => { if (!destroyed) schedule(true); });
    measure();
    revealHash(false);
    paint();
  }

  function init(scope = document) {
    if (scope.matches?.(ROOT)) create(scope);
    scope.querySelectorAll(ROOT).forEach(create);
  }
  function destroy(scope = document) {
    for (const [root, instance] of instances) {
      if (scope === root || scope.contains(root)) { instance.destroy(); instances.delete(root); }
    }
  }
  function refresh() { destroy(); init(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  [reduced, contrast].forEach(query => {
    if (query.addEventListener) query.addEventListener('change', refresh);
    else if (query.addListener) query.addListener(refresh);
  });
  const observeMotion = () => {
    const observer = new MutationObserver(refresh);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-lrs-motion'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeMotion, { once: true });
  else observeMotion();
  window.LRSEditorials = { init, refresh, destroy };
})();
